import { useLayoutEffect } from 'react';
import type { Account } from './api/session';
import type { Schema } from './api/client';
import {
  DEFAULT_APPEARANCE_COLOR,
  deriveAppearanceColors,
  normalizeHexColor,
} from './appearance-colors';

type Owner = Pick<Account, 'id' | 'workspaceId'>;
const cacheKey = (owner: Owner) => `snaphire.appearance.v1:${owner.workspaceId}:${owner.id}`;

export function applyInterfaceColor(value: unknown): void {
  const colors = deriveAppearanceColors(normalizeHexColor(value) || DEFAULT_APPEARANCE_COLOR);
  for (const [property, color] of Object.entries(colors.cssVariables)) {
    document.documentElement.style.setProperty(property, color);
  }
  document.documentElement.style.setProperty('--ambient-color', `rgb(${colors.ambientRgb})`);
}

export function readAppearanceCache(owner: Owner): string | null {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(cacheKey(owner)) || 'null');
    if (!value || typeof value !== 'object' || !('version' in value) || value.version !== 1)
      return null;
    return 'color' in value ? normalizeHexColor(value.color) : null;
  } catch {
    return null;
  }
}

export function cacheAppearanceColor(owner: Owner, value: unknown): void {
  try {
    const color = normalizeHexColor(value);
    if (!color || color === DEFAULT_APPEARANCE_COLOR) localStorage.removeItem(cacheKey(owner));
    else localStorage.setItem(cacheKey(owner), JSON.stringify({ version: 1, color }));
  } catch {
    // Серверная настройка продолжает работать, если браузер запрещает localStorage.
  }
}

export function useInterfaceAppearance(
  account: Account | null,
  user?: Schema<'CurrentUserResponse'>,
): void {
  const accountId = account?.id;
  const workspaceId = account?.workspaceId;
  const serverColor =
    accountId && user?.account.id === accountId && user.account.workspaceId === workspaceId
      ? (user.appearance?.color ?? null)
      : undefined;
  useLayoutEffect(() => {
    if (!accountId || !workspaceId) {
      applyInterfaceColor(null);
      return;
    }
    const owner = { id: accountId, workspaceId };
    if (serverColor === undefined) applyInterfaceColor(readAppearanceCache(owner));
    else {
      applyInterfaceColor(serverColor);
      cacheAppearanceColor(owner, serverColor);
    }
  }, [accountId, workspaceId, serverColor]);
}
